import { CellContext } from '@tanstack/react-table'
import { type ClassValue, clsx } from 'clsx'
import {
  differenceInDays,
  format,
  formatDistanceToNow,
  fromUnixTime,
  isFuture,
} from 'date-fns'
import { twMerge } from 'tailwind-merge'

import { Group } from '@droidmap/organisation-service-contract'
import { SUPPORT_USER_ID } from '@droidmap/permissions-service-contract'
import { isMacOSMetadataFile } from '@droidmap/shared-utils'
import { User } from '@droidmap/user-service-contract'

/**
 * Combines multiple class names into a single string.
 *
 * @param {...ClassValue[]} inputs - The class names to combine.
 * @returns {string} The combined class names.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Type definition for a debounced function.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DebouncedFunction<T extends (...args: any[]) => void> = (
  ...args: Parameters<T>
) => void

/**
 * Creates a debounced function that delays invoking the provided function until
 * after the specified wait time has elapsed since the last time the debounced
 * function was invoked.
 *
 * @param {T} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {DebouncedFunction<T>} The debounced function.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number,
): DebouncedFunction<T> => {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Title-cases a slug, replacing `-`/`+` separators with spaces and capitalising
 * each word — e.g. `field-high-resolution` → `Field High Resolution`.
 */
export const titleCase = (value: string): string =>
  value.replace(/[-+]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())

// Re-exported rather than defined here: the staff console renders the same
// organisation's figures, and it now reads them from the same place. Existing
// callers keep importing from `@/lib/utils`.
export { formatBytes, formatCents } from '@droidmap/frontend-ui'

/**
 * Formats the given number of bits into a human-readable string representation.
 *
 * @param {number} bits - The number of bits to format.
 * @param {number} [decimals=2] - The number of decimal places to round to.
 * @returns {string} The formatted string representation of the bits.
 */
export function formatBits(bits: number, decimals = 2): string {
  if (!+bits) return '0 Bits'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bits', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bits) / Math.log(k))

  return `${parseFloat((bits / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Formats the given number of bits into a human-readable string representation.
 *
 * @param {number} bits - The number of bits to format.
 * @param {number} [decimals=2] - The number of decimal places to round to.
 * @returns {string} The formatted string representation of the bits.
 */
export function formatBitsPerSecond(bits: number, decimals = 2): string {
  if (!+bits) return '0 Bps'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = [
    'bps',
    'Kbps',
    'Mbps',
    'Gbps',
    'Tbps',
    'Pbps',
    'Ebps',
    'Zbps',
    'Ybps',
  ]

  const i = Math.floor(Math.log(bits) / Math.log(k))

  return `${parseFloat((bits / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Returns the dirty values from the given dirty fields and values.
 * Dirty values are the values that have been modified or marked as dirty in the dirty fields.
 *
 * @template DirtyFields - The type of the dirty fields.
 * @template Values - The type of the values.
 * @param dirtyFields - The dirty fields object.
 * @param values - The values object.
 * @returns The partial object of the dirty values.
 */
export function getDirtyValues<
  DirtyFields extends Record<string, unknown>,
  Values extends Partial<Record<keyof DirtyFields, unknown>>,
>(dirtyFields: DirtyFields, values: Values): Partial<typeof values> {
  const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
    // Unsure when RFH sets this to `false`, but omit the field if so.
    if (!dirtyFields[key]) return prev

    return {
      ...prev,
      [key]:
        typeof dirtyFields[key] === 'object'
          ? getDirtyValues(
              dirtyFields[key] as DirtyFields,
              values[key] as Values,
            )
          : values[key],
    }
  }, {})

  return dirtyValues
}

/**
 * Returns whether the session should be refreshed based on the issue time and expiration time.
 *
 * @param issueTime - The time the session was issued.
 * @param expirationTime - The time the session will expire.
 * @returns boolean
 */
export function shouldRefresh(
  issueTime: number,
  expirationTime: number,
): boolean {
  const currentTime = Math.floor(Date.now() / 1000)
  const sessionLifetime = expirationTime - issueTime
  const remainingTime = expirationTime - currentTime
  const percentageRemaining = (remainingTime / sessionLifetime) * 100

  return percentageRemaining < 25
}

/**
 * Formats the time difference between two timestamps into a human-readable string.
 *
 * @param start - The start timestamp in milliseconds.
 * @param end - The end timestamp in milliseconds.
 * @returns A string representing the time difference in days, hours, minutes, and seconds.
 *
 * @example
 * ```typescript
 * const start = Date.now();
 * const end = start + 90061000; // 1 day, 1 hour, 1 minute, and 1 second later
 * console.log(formatTimeDifference(start, end)); // "1d 1h 1m 1s"
 * ```
 */
export function formatTimeDifference(start: number, end: number): string {
  const diff = Math.abs(end - start)
  const seconds = Math.floor(diff / 1000)

  const d = Math.floor(seconds / (3600 * 24))
  const h = Math.floor((seconds % (3600 * 24)) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60

  const parts: string[] = []

  if (d > 0) parts.push(`${d}d`)
  if (h > 0) parts.push(`${h}h`)
  if (m > 0) parts.push(`${m}m`)
  if (s > 0) parts.push(`${s}s`)

  return parts.join(' ') || '0s'
}

/**
 * Returns the initials of the given user.
 *
 * @param user - The user to get the initials of.
 * @returns The initials of the user.
 */
export function getInitials(user: User): string {
  const firstInitial = user.firstName?.charAt(0)?.toUpperCase() || ''
  const lastInitial = user.lastName?.charAt(0)?.toUpperCase() || ''
  return `${firstInitial}${lastInitial}`
}

/**
 * Returns the given user's name for display. A name the account has not
 * supplied is left out rather than rendered, so a half-populated user reads as
 * the name it does have, and one with neither reads as nothing at all.
 *
 * @param user - The user to name.
 * @returns The user's full name, or an empty string when it has no name.
 */
export function getDisplayName(user: User): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ')
}

/**
 * Returns the full name of a user from a userMap and userId, or 'unknown' if not found.
 *
 * For use with our useOrganisationUsersWithUserMap hook which returns a userMap for
 * the users in the organisation.
 *
 * @param userMap - A map of userId to user object.
 * @param userId - The userId to look up.
 * @returns The user's full name or 'unknown'.
 */
export function getUserFullName(
  userMap: Record<string, User> | undefined,
  userId: string,
): string {
  // Special case for system user
  if (userId === 'system') return 'System'
  // Changes made in an assist session (droidmap#2651)
  if (userId === SUPPORT_USER_ID) return 'DroidMap Support'

  const user = userMap?.[userId]
  return user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'unknown'
    : 'unknown'
}

/**
 * Returns the email of a user from a userMap and userId, or 'unknown' if not found.
 */
export function getUserEmail(
  userMap: Record<string, User> | undefined,
  userId: string,
): string {
  const user = userMap?.[userId]
  return user?.userEmail || 'unknown'
}

/**
 * Returns the full name of a user from a userMap and userId, or 'unknown' if not found.
 *
 * For use within ColumnDef's to make the displaying of user names easier
 *
 * @param info
 * @param userId
 * @returns
 */
export function getUserFullNameFromCellContext<T>(
  info: CellContext<T, keyof T>,
  userId: string,
): string {
  const userMap = info.table.options.meta?.userMap
  return getUserFullName(userMap, userId)
}

export function getUserEmailFromCellContext<T>(
  info: CellContext<T, keyof T>,
  userId: string,
): string {
  const userMap = info.table.options.meta?.userMap
  return getUserEmail(userMap, userId)
}

export function getGroupName(
  groupMap: Record<string, Group> | undefined,
  groupId: string,
): string {
  const group = groupMap?.[groupId]
  return group?.name || 'unknown'
}

export function getGroupNameFromCellContext<T>(
  info: CellContext<T, keyof T>,
  groupId: string,
): string {
  const groupMap = info.table.options.meta?.groupMap
  return getGroupName(groupMap, groupId)
}

/**
 * Filters out macOS metadata files from a list of files.
 *
 * macOS creates metadata files alongside real files:
 * - `.DS_Store` - Directory metadata files
 * - `._*` - AppleDouble files containing resource forks and extended attributes
 *
 * These files are not actual image/asset files and cause issues with
 * image dimension calculation and job size estimation.
 *
 * @param files - Array of File objects to filter
 * @returns Filtered array with macOS metadata files removed
 *
 * @example
 * ```typescript
 * const files = [
 *   new File([''], 'image.jpg'),
 *   new File([''], '._image.jpg'),
 *   new File([''], '.DS_Store')
 * ];
 * const filtered = filterMacOSMetadataFiles(files);
 * // Returns: [File('image.jpg')]
 * ```
 */
export function filterMacOSMetadataFiles(files: File[]): File[] {
  return files.filter((file) => !isMacOSMetadataFile(file.name))
}

const DATE_FORMAT = 'MMM d, yyyy'
const DATE_TIME_FORMAT = 'M/d/yy, h:mm a'
const DEFAULT_RELATIVE_FORMATTER = (relative: string) => relative

interface FormatDateOptions {
  /** Custom format string (date-fns format) */
  format?: string
  /** Shorthand for DATE_TIME_FORMAT */
  includeTime?: boolean
  /** Treat number as Unix seconds (not milliseconds) */
  isUnixSeconds?: boolean
  /** Add relative prefix for future dates within 7 days (e.g., "in 3 days") */
  relative?: boolean
  /** Format function for combining relative and formatted date. Default: returns relative only */
  relativeFormatter?: (relative: string, formatted: string) => string
}

/**
 * Formats a date into a human-readable string.
 * @param date - Date object, ISO string, or Unix timestamp
 * @returns Formatted date string (e.g., "Jun 15, 2024" or "in 3 days")
 */
export const formatDate = (
  date: Date | string | number,
  options: FormatDateOptions = {},
): string => {
  const d =
    typeof date === 'number' && options.isUnixSeconds
      ? fromUnixTime(date)
      : new Date(date)

  const formatStr =
    options.format ?? (options.includeTime ? DATE_TIME_FORMAT : DATE_FORMAT)
  const formatted = format(d, formatStr)

  if (!options.relative || !isFuture(d)) return formatted

  const days = differenceInDays(d, new Date())
  if (days >= 7) return formatted

  const relative = formatDistanceToNow(d, { addSuffix: true })
  const formatter = options.relativeFormatter ?? DEFAULT_RELATIVE_FORMATTER
  return formatter(relative, formatted)
}

/** A credit's price: `0.875` stays three places, `1` reads as `$1.00`. */
export const formatCreditPrice = (price: string): string => {
  const value = Number(price)
  return `$${value.toFixed(Number((value * 100).toFixed(4)) % 1 === 0 ? 2 : 3)}`
}
